import { push } from 'react-router-redux';
import {
  assoc,
  indexBy,
  map,
  omit,
  path,
  pick,
  pipe,
  prop,
  tap,
  unless,
  identity,
} from 'ramda';
import querystring from 'query-string';
import {
  SET_CAMPAIGN_TYPE,
  SET_CAMPAIGN_PROMO_ID,
  CLEAR_PROMO,
  TOGGLE_CAMPAIGN_SEND_EMAIL,
  TOGGLE_CAMPAIGN_SEND_SMS,
  TOGGLE_CAMPAIGN_SEND_PUSH,
  SET_CUSTOMER_GROUPS,
  SET_NEW_CUSTOMER_GROUP_WEEKS,
  SET_CAMPAIGN_CUSTOMER_GROUP,
  TOGGLE_CAMPAIGN_CUSTOMER_GROUP,
  SET_CAMPAIGN_SUBJECT,
  SET_CAMPAIGN_BODY,
  SET_CAMPAIGN_LOGO,
  SET_CAMPAIGN_LOGO_IMAGE,
  SET_CAMPAIGN_BACKGROUND,
  SET_CAMPAIGN_BACKGROUND_IMAGE,
  SET_CAMPAIGN_COLOR,
  SAVE_CAMPAIGN,
  FETCH_CAMPAIGN_LIST,
  CLEAR_CAMPAIGN_LIST,
  SET_CAMPAIGN_OWNER_ID,
  SET_CAMPAIGN_OWNER_TYPE,
  FETCH_CAMPAIGN,
  SET_CAMPAIGN,
  RESET_CAMPAIGN,
  SET_SCHEDULED_AT,
  DELETE_CAMPAIGN,
  SET_FILTER,
  AUDIENCE_TYPE,
  SET_SERVICE_CUSTOMER_GROUP_SERVICE_ID,
  SET_RECURRING_START_DATE,
  SET_RECURRING_END_DATE,
  SET_RECURRING_TIME,
  SET_RECURRING_FREQUENCY,
  SET_RECURRING_SEND_TO_ALL_CUSTOMERS,
  SET_SEARCH_STRING,
} from './constants';
import Http from '../http';
import { renamePropIfExists } from '../common/helpers/functional';
import { getByType } from './selectors';
import { refetchCampaignsListQuery } from '../../modules/shared/engage/network/utils';

export const setCampaignType = type => ({
  type: SET_CAMPAIGN_TYPE,
  payload: type,
});

export const setPromoId = id => ({
  type: SET_CAMPAIGN_PROMO_ID,
  payload: id,
});

export const clearPromo = () => ({
  type: CLEAR_PROMO,
  payload: null,
});

export const toggleSendEmail = () => ({
  type: TOGGLE_CAMPAIGN_SEND_EMAIL,
});

export const toggleSendSms = () => ({
  type: TOGGLE_CAMPAIGN_SEND_SMS,
});

export const toggleSendPush = () => ({
  type: TOGGLE_CAMPAIGN_SEND_PUSH,
});

const fetchCustomerGroupsAction = groups => ({
  type: SET_CUSTOMER_GROUPS,
  payload: groups,
});

const mapId = map(getByType(
  renamePropIfExists('id', 'barberId'),
  renamePropIfExists('id', 'shopId'),
  identity
));
const indexByNameOrBarberId = indexBy(getByType(
  prop('barberId'),
  prop('shopId'),
  prop('name'),
));
const emptyServiceGroup = { name: 'service', customers: 0 };
const alwaysServiceGroup = unless(prop('service'), assoc('service', emptyServiceGroup));
const transformCustomerGroups = pipe(indexByNameOrBarberId, alwaysServiceGroup);
const dispatchFetchCustomerAction = dispatch =>
  pipe(mapId, transformCustomerGroups, fetchCustomerGroupsAction, dispatch);
const fetchCustomerGroups = getUrl => (id, params) => (dispatch) => {
  const pathname = getUrl(id);
  const queryString = querystring.stringify(params);
  const url = [pathname, queryString].filter(Boolean).join('?');
  Http.get(url).then(dispatchFetchCustomerAction(dispatch));
};

export const fetchShopCustomerGroups = fetchCustomerGroups(id => `/shop/${id}/grouped-customer`);
export const fetchBarberCustomerGroups = fetchCustomerGroups(id => `/barber/${id}/grouped-customer`);
export const fetchBrandCustomerGroups = fetchCustomerGroups(id => `/brand/${id}/grouped-customer`);

export const setNewCustomerWeeks = weeks => ({
  type: SET_NEW_CUSTOMER_GROUP_WEEKS,
  payload: weeks,
});

export const setServiceCustomerGroupServiceId = serviceId => ({
  type: SET_SERVICE_CUSTOMER_GROUP_SERVICE_ID,
  payload: serviceId,
});

export const setCustomerGroup = group => ({
  type: SET_CAMPAIGN_CUSTOMER_GROUP,
  payload: [group],
});

export const unsetCustomerGroup = () => ({
  type: SET_CAMPAIGN_CUSTOMER_GROUP,
  payload: [],
});

export const toggleCustomerGroup = (group, weeks) => ({
  type: TOGGLE_CAMPAIGN_CUSTOMER_GROUP,
  payload: {
    ...group,
    ...(group.name === AUDIENCE_TYPE.new && { weeks }),
  },
});

export const setSubject = text => ({
  type: SET_CAMPAIGN_SUBJECT,
  payload: text,
});

export const setBody = text => ({
  type: SET_CAMPAIGN_BODY,
  payload: text,
});

export const setLogo = imageFile => ({
  type: SET_CAMPAIGN_LOGO,
  payload: imageFile,
});

export const setLogoImage = imageUrl => ({
  type: SET_CAMPAIGN_LOGO_IMAGE,
  payload: imageUrl,
});

export const setBackground = imageFile => ({
  type: SET_CAMPAIGN_BACKGROUND,
  payload: imageFile,
});

export const setBackgroundImage = imageUrl => ({
  type: SET_CAMPAIGN_BACKGROUND_IMAGE,
  payload: imageUrl,
});

export const setColor = color => ({
  type: SET_CAMPAIGN_COLOR,
  payload: color,
});

const saveCampaignAction = campaign => ({
  type: SAVE_CAMPAIGN,
  payload: campaign,
});

const campaignFields = [
  'ownerId',
  'ownerType',
  'type',
  'promoId',
  'promo',
  'customerGroups',
  'subject',
  'body',
  'color',
  'sendEmail',
  'sendPush',
  'sendSms',
  'logo',
  'background',
];

const parseCustomerGroups = (data) => {
  const customerGroups = data.customerGroups;
  const result = {};
  customerGroups.forEach((item, index) => {
    Object.keys(item).forEach((key) => {
      result[`customerGroups[${index}][${key}]`] = item[key];
    });
  });

  return {
    ...omit(['customerGroups'], data),
    ...result,
  };
};
const prepareCampaignFields = pipe(pick(campaignFields), parseCustomerGroups);

const invalidateCampaigns = async (result) => {
  refetchCampaignsListQuery();
  return result;
}

export const saveCampaign = campaign => dispatch =>
  Http
    .postRaw('/campaign', prepareCampaignFields(campaign))
    .then(invalidateCampaigns)
    .then(tap(pipe(saveCampaignAction, dispatch)));

export const updateCampaign = campaign => dispatch =>
  Http
    .putRaw(`/campaign/${campaign.id}`, prepareCampaignFields(campaign))
    .then(invalidateCampaigns)
    .then(tap(pipe(saveCampaignAction, dispatch)));

const deleteCampaignAction = campaign => ({
  type: DELETE_CAMPAIGN,
  payload: campaign,
});

export const deleteCampaign = campaign => dispatch =>
  Http
    .delete(`/campaign/${campaign.id}`)
    .then(invalidateCampaigns)
    .then(() => dispatch(deleteCampaignAction(campaign)));

export const pauseCampaign = campaign => () => (
  Http
    .post(
      `/campaign/${campaign.id}/reschedule`,
      { recurringState: 'stop' }
    )
    .then(invalidateCampaigns)
);

export const resumeCampaign = campaign => () => (
  Http
    .post(
      `/campaign/${campaign.id}/reschedule`,
      { recurringState: 'start' }
    )
    .then(invalidateCampaigns)
);

export const redirectToCampaignCharge = ({ id }, activeType) => (dispatch) => {
  let url = `/engagement/${id}/charge`;
  if (activeType === 'barber') {
    url = `/barber${url}`;
  }
  if (activeType === 'brand') {
    url = `/brand${url}`;
  }
  return dispatch(push(url));
};

const clearCampaignsAction = () => ({
  type: CLEAR_CAMPAIGN_LIST,
});

const fetchCampaignsAction = campaignRows => ({
  type: FETCH_CAMPAIGN_LIST,
  payload: campaignRows,
});

const fetchCampaigns = url => (dispatch) => {
  dispatch(clearCampaignsAction());
  return Http.get(url).then(pipe(prop('rows'), fetchCampaignsAction, dispatch));
};
export const fetchShopCampaigns = id => fetchCampaigns(`/shop/${id}/campaign`);
export const fetchBarberCampaigns = id => fetchCampaigns(`/barber/${id}/campaign`);
export const fetchBrandCampaigns = id => fetchCampaigns(`/brand/${id}/campaign`);

const fetchCampaignAction = campaign => ({
  type: FETCH_CAMPAIGN,
  payload: campaign,
});

const fetchCampaign = url => dispatch => Http.get(url).then(tap(pipe(fetchCampaignAction, dispatch)));
export const fetchShopCampaign = (shopId, campaignId) => fetchCampaign(`/shop/${shopId}/campaign/${campaignId}`);
export const fetchBarberCampaign = (barberId, campaignId) =>
  fetchCampaign(`/barber/${barberId}/campaign/${campaignId}`);
export const fetchBrandCampaign = (brandId, campaignId) => fetchCampaign(`/brand/${brandId}/campaign/${campaignId}`);

export const setOwnerId = id => ({
  type: SET_CAMPAIGN_OWNER_ID,
  payload: id,
});

export const setOwnerType = type => ({
  type: SET_CAMPAIGN_OWNER_TYPE,
  payload: type,
});

export const resetCampaign = () => ({
  type: RESET_CAMPAIGN,
});

export const setCampaign = campaign => ({
  type: SET_CAMPAIGN,
  payload: campaign,
});

const campaignDraftFields = [
  'id',
  'type',
  'subject',
  'body',
  'color',
  'price',
  'customersNotifiedViaEmail',
  'customersNotifiedViaSms',
  'customersNotifiedViaPush',
  'totalCustomersNotified',
  'customerGroups',
  'sendEmail',
  'sendSms',
  'sendPush',
  'backgroundImage',
  'logoImage',
  'promoId',
  'promo',
  'recurringTime',
  'recurringFrequency',
  'recurringStartDate',
  'recurringEndDate',
  'recurringNextOccurrenceDateTime',
];

const setDraftLogoImage = campaign => assoc('logoImage', path(['logo', 'url'], campaign))(campaign);
const setDraftBackgroundImage = campaign => assoc('backgroundImage', path(['background', 'url'], campaign))(campaign);
export const setDraftCampaign = pipe(
  setDraftLogoImage,
  setDraftBackgroundImage,
  pick(campaignDraftFields),
  setCampaign,
);

export const redirectToCampaignList = activeType => (dispatch) => {
  let url = '/engagement';
  if (activeType === 'barber') {
    url = `/barber${url}`;
  }
  if (activeType === 'brand') {
    url = `/brand${url}`;
  }
  return dispatch(push(url));
};

export const chargeCampaign = (id, data) => () => Http
  .post(`/campaign/${id}/charge`, data)
  .then(invalidateCampaigns);

export const setScheduledAt = dateTime => ({
  type: SET_SCHEDULED_AT,
  payload: dateTime,
});

export const setRecurringTime = dateTime => ({
  type: SET_RECURRING_TIME,
  payload: dateTime,
});

export const setFilter = filter => ({
  type: SET_FILTER,
  payload: filter,
});

export const setSearchString = searchString => ({
  type: SET_SEARCH_STRING,
  payload: searchString,
});

export const setRecurringStartDate = date => ({
  type: SET_RECURRING_START_DATE,
  payload: date,
});

export const setRecurringEndDate = date => ({
  type: SET_RECURRING_END_DATE,
  payload: date,
});

export const setRecurringFreqeuncy = frequency => ({
  type: SET_RECURRING_FREQUENCY,
  payload: frequency,
});

export const setRecurringSendToAllCustomers = sendToAll => ({
  type: SET_RECURRING_SEND_TO_ALL_CUSTOMERS,
  payload: sendToAll,
});
